import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/base/AnimeCard";
import { categories, animeList } from "@/mocks/anime";

const statusTabs = [
  { key: "all", label: "全部" },
  { key: "ongoing", label: "连载中" },
  { key: "finished", label: "已完结" },
];

const sortTabs = [
  { key: "hot", label: "热门" },
  { key: "rating", label: "评分" },
  { key: "new", label: "年份" },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((c) => c.type === type);
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("hot");

  useEffect(() => {
    setStatus("all");
    setSort("hot");
    if (category) {
      document.title = `${category.name} - ${category.tagline} | 妖精动漫网`;
    }
    window.scrollTo({ top: 0 });
  }, [type]);

  const list = useMemo(() => {
    let base = animeList.filter((a) => a.category === type);
    if (status === "ongoing") base = base.filter((a) => a.status === "连载中");
    if (status === "finished") base = base.filter((a) => a.status === "已完结");
    if (sort === "hot") {
      base = [...base].sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    } else if (sort === "rating") {
      base = [...base].sort((a, b) => b.rating - a.rating);
    } else {
      base = [...base].sort((a, b) => b.year - a.year);
    }
    return base;
  }, [type, status, sort]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex flex-col items-center justify-center pt-20 bg-background-50">
          <span className="w-16 h-16 rounded-2xl bg-background-100 text-foreground-300 flex items-center justify-center text-3xl">
            <i className="ri-folder-info-line"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">该分类不存在</h1>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 text-sm">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} | 妖精动漫网`,
            description: category.desc,
            url: `https://updateline.github.io/category/${category.type}`,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: list.map((a, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: a.title,
              })),
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://updateline.github.io/" },
                { "@type": "ListItem", position: 2, name: category.name },
              ],
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <section className="relative pt-20 h-64 md:h-72 flex items-end overflow-hidden">
          <img
            src={category.image}
            alt={`${category.name} 分类横幅`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-foreground-950/40"></div>
          <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <nav aria-label="面包屑" className="mb-3">
              <ol className="flex items-center gap-2 text-sm text-white/80">
                <li>
                  <Link to="/" className="hover:text-white">首页</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-white">{category.name}</li>
              </ol>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl text-white">{category.name}</h1>
            <p className="mt-2 text-sm md:text-base text-accent-200">{category.tagline}</p>
            <p className="mt-2 text-sm text-white/80 max-w-2xl leading-relaxed">{category.desc}</p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div className="inline-flex items-center gap-1 p-1 bg-background-100 rounded-full border border-background-200 self-start">
                {statusTabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setStatus(t.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      status === t.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex items-center gap-1 p-1 bg-background-100 rounded-full border border-background-200 self-start">
                {sortTabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setSort(t.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      sort === t.key
                        ? "bg-secondary-500 text-background-50"
                        : "text-foreground-600 hover:text-secondary-600"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <p className="mb-6 text-sm text-foreground-500">
              共收录 <span className="text-primary-600 font-semibold">{list.length}</span> 部{category.name}
            </p>

            {list.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {list.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-background-100 rounded-2xl border border-background-200">
                <span className="mx-auto w-16 h-16 rounded-2xl bg-background-50 text-foreground-300 flex items-center justify-center text-3xl">
                  <i className="ri-inbox-2-line"></i>
                </span>
                <p className="mt-4 text-foreground-600 text-sm">当前筛选下暂无番剧</p>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-background-200">
              <h2 className="font-heading text-xl text-foreground-950 mb-4">浏览其他题材</h2>
              <div className="flex flex-wrap gap-3">
                {categories
                  .filter((c) => c.type !== type)
                  .map((c) => (
                    <Link
                      key={c.type}
                      to={`/category/${c.type}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      <i className={c.icon}></i>
                      {c.name}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}